import { LanguageProvider } from "./lib/i18n";
import React, { useEffect, useState } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from './firebaseClient';

// Named Imports
import { Dashboard } from './components/Dashboard';
import { Properties } from './components/Properties';
import { Tenants } from './components/Tenants';
import { Leases } from './components/Leases';
import { Documents } from './components/Documents';
import { BulkLeaseUploader } from './components/BulkLeaseUploader';
import { Account } from './components/Account';
import { DealSourcing } from './components/DealSourcing';
import { UnderwritingToolkit } from './components/UnderwritingToolkit';

// Default Imports
import LeaseAbstraction from './components/LeaseAbstraction';
import Valuation from './components/Valuation';
import MarketAnalysis from './components/MarketAnalysis';
import PredictiveTrends from './components/PredictiveTrends';
import PropertyDetailView from './components/PropertyDetailView';
import BulkLeaseValidator from './components/BulkLeaseValidator';
import CalendarViewWrapper from './components/CalendarViewWrapper';
import Auth from './components/Auth';
import HomePage from './components/HomePage'; 
import Navbar from './components/Navbar'; 
import InsufficientCreditsModal from './components/InsufficientCreditsModal';
import FAQ from './components/FAQ';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import Contact from './components/Contact';
import { GlobalModal } from './components/GlobalModal'; // ADDED THIS

import { Routes, Route, Navigate } from 'react-router-dom';
import BlogListPage from './pages/blog/BlogListPage';
import ArticleDetailPage from './pages/blog/ArticleDetailPage';
import DemoPage from './pages/DemoPage';
import AboutPage from './pages/AboutPage';
import './App.css';

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Firebase auth state listener
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    await auth.signOut();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <LanguageProvider>
    <div className="App">
        <GlobalModal />
        <Routes>
            {/* Public Routes */}
            <Route path="/" element={!user ? <HomePage /> : <Navigate to="/dashboard" />} />
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/:slug" element={<ArticleDetailPage />} />
            <Route path="/login" element={!user ? <Auth /> : <Navigate to="/dashboard" />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/demo" element={<DemoPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/walkthrough" element={<DemoPage />} />

            {/* Protected Routes Wrapper */}
            <Route path="/*" element={
                user ? (
                    <div className="flex flex-col min-h-screen">
                        <Navbar handleSignOut={handleLogout} />
                        <InsufficientCreditsModal />
                        <main className="flex-1 overflow-auto">
                            <Routes>
                                <Route path="/dashboard" element={<Dashboard user={user} />} />
                                <Route path="/properties" element={<Properties />} />
                                <Route path="/tenants" element={<Tenants />} />
                                <Route path="/leases" element={<Leases />} />
                                <Route path="/documents" element={<Documents />} />
                                <Route path="/lease-abstraction" element={<LeaseAbstraction />} />
                                <Route path="/bulk-leases" element={<BulkLeaseUploader />} />
                                <Route path="/bulk-leases/:jobId" element={<BulkLeaseValidator />} />
                                <Route path="/account" element={<Account />} />
                                <Route path="/valuation" element={<Valuation />} />
                                <Route path="/market-analysis" element={<MarketAnalysis />} />
                                <Route path="/predictive-trends" element={<PredictiveTrends />} />
                                <Route path="/properties/:id" element={<PropertyDetailView />} />
                                <Route path="/properties/:propertyId/valuation" element={<Valuation />} />
                                <Route path="/properties/:propertyId/market-analysis" element={<MarketAnalysis />} />
                                <Route path="/properties/:propertyId/trends" element={<PredictiveTrends />} />
                                <Route path="/deal-sourcing" element={<DealSourcing />} />
                                <Route path="/underwriting" element={<UnderwritingToolkit />} />
                                <Route path="/calendar" element={<CalendarViewWrapper />} />
                                {/* Redirect root dashboard hits */}
                                <Route path="*" element={<Navigate to="/dashboard" />} />
                            </Routes>
                        </main>
                    </div>
                ) : (
                    <Navigate to="/login" />
                )
            } />
        </Routes>
    </div>
    </LanguageProvider>
  );
}

export default App;
